import React, { useEffect, useState } from 'react';
import { PieChart, RefreshCw } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const ReportingRecrutement = () => {
  const { filialeId } = useFiliale();
  const [stats, setStats] = useState(null);
  const [byStatut, setByStatut] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = filialeId && filialeId !== 'all' ? { filiale_id: filialeId } : {};
      const [summary, candidatures] = await Promise.all([
        http.get('/api/offres/stats/summary', { params }),
        http.get('/api/candidatures', { params }),
      ]);
      setStats(summary.data);
      const list = Array.isArray(candidatures.data) ? candidatures.data : [];
      const map = {};
      list.forEach((c) => {
        map[c.statut] = (map[c.statut] || 0) + 1;
      });
      setByStatut(Object.entries(map).map(([statut, count]) => ({ statut, count })));
    } catch (e) {
      console.error(e);
      setStats(null);
      setByStatut([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filialeId]);

  const cards = [
    { label: 'Total offres', value: stats?.total_offres },
    { label: 'Publiées', value: stats?.publiees },
    { label: 'Brouillons', value: stats?.brouillons },
    { label: 'Fermées', value: stats?.fermees },
    { label: 'Pourvues', value: stats?.pourvues },
    { label: 'Candidatures', value: stats?.total_candidatures },
    { label: 'En entretien', value: stats?.entretiens },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Reporting recrutement"
        subtitle="Indicateurs du pipeline"
        icon={PieChart}
        actions={[{ label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false }]}
      />

      <div className="px-6 py-8 space-y-6">
        {loading ? (
          <div className="text-sm text-gray-500">Chargement…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {cards.map((c) => (
                <div key={c.label} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">{c.label}</div>
                  <div className="mt-1 text-2xl font-bold text-blue-900">{Number(c.value) || 0}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Candidatures par statut</h3>
              {byStatut.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune candidature pour le moment.</p>
              ) : (
                <div className="space-y-3">
                  {byStatut.map((row) => {
                    const total = byStatut.reduce((s, r) => s + r.count, 0) || 1;
                    const pct = Math.round((row.count / total) * 100);
                    return (
                      <div key={row.statut}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize text-gray-700">{row.statut.replace(/_/g, ' ')}</span>
                          <span className="font-medium">{row.count} ({pct}%)</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-sky-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportingRecrutement;
